import { useEffect, useState } from "react";
import { icons } from "./assets";
import AdminDashboard from "./AdminDashboard";
import AdminShell from "./admin/AdminShell";
import EmailCampaigns from "./admin/EmailCampaigns";
import EmailPreview from "./admin/EmailPreview";
import AdminSettings from "./admin/AdminSettings";
import AdminShopSettings from "./admin/AdminShopSettings";
import AdminOrders from "./admin/AdminOrders";
import AdminBookings from "./admin/AdminBookings";
import AdminProducts from "./admin/AdminProducts";
import { getAdminSection } from "./admin/adminRouting";
import {
  adminLogin,
  clearAdminToken,
  deleteSubscriber,
  fetchSubscribers,
  getAdminToken,
  getAdminUsername,
  importSubscribers,
  sendSubscriberEmail,
  setAdminToken,
  setAdminUsername,
  updateSubscriberStatus,
} from "./api";

function AdminPanel() {
  const [token, setToken] = useState(() => getAdminToken());
  const [adminUsername, setAdminUsernameState] = useState(() => getAdminUsername());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [section, setSection] = useState(() => getAdminSection());

  useEffect(() => {
    function syncSection() {
      setSection(getAdminSection());
    }

    window.addEventListener("popstate", syncSection);
    return () => window.removeEventListener("popstate", syncSection);
  }, []);

  useEffect(() => {
    document.title = "Mizu Admin";
    document.documentElement.lang = "hu";

    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex, nofollow";
  }, []);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function load(silent = false) {
      if (!silent) setLoadingList(true);

      try {
        const data = await fetchSubscribers(token);
        if (cancelled) return;
        setSubscribers(data.subscribers);
        setLastRefreshedAt(new Date());
      } catch {
        if (cancelled) return;
        clearAdminToken();
        setToken(null);
        setAdminUsernameState(null);
        setLoadError("A munkamenet lejárt. Jelentkezz be újra.");
      } finally {
        if (!cancelled && !silent) setLoadingList(false);
      }
    }

    load(false);

    const interval = window.setInterval(() => load(true), 30000);

    function handleVisibility() {
      if (document.visibilityState === "visible") {
        load(true);
      }
    }

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [token]);

  async function refreshSubscribers() {
    if (!token) return;

    setRefreshing(true);
    try {
      const data = await fetchSubscribers(token);
      setSubscribers(data.subscribers);
      setLastRefreshedAt(new Date());
    } catch {
      clearAdminToken();
      setToken(null);
      setAdminUsernameState(null);
      setLoadError("A munkamenet lejárt. Jelentkezz be újra.");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleDeleteSubscriber(email) {
    if (!token) return;

    try {
      await deleteSubscriber(token, email);
      setSubscribers((current) => current.filter((entry) => entry.email !== email));
      setLastRefreshedAt(new Date());
    } catch {
      throw new Error("delete_failed");
    }
  }

  async function handleUpdateSubscriberStatus(email, updates) {
    if (!token) return;

    const data = await updateSubscriberStatus(token, email, updates);
    setSubscribers((current) =>
      current.map((entry) =>
        entry.email === email ? { ...entry, ...data.subscriber } : entry,
      ),
    );
    setLastRefreshedAt(new Date());
  }

  async function handleImportSubscribers(payload) {
    if (!token) return;

    const result = await importSubscribers(token, payload);
    const data = await fetchSubscribers(token);
    setSubscribers(data.subscribers);
    setLastRefreshedAt(new Date());
    return result;
  }

  async function handleSendOpeningEmail(email) {
    if (!token) return;

    const result = await sendSubscriberEmail(token, {
      email,
      emailType: "opening_announcement",
    });

    setSubscribers((current) =>
      current.map((entry) =>
        entry.email === email
          ? { ...entry, customerNumber: result.customerNumber }
          : entry,
      ),
    );
    setLastRefreshedAt(new Date());
    return result;
  }

  async function handleLogin(event) {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError("");

    const trimmedUsername = username.trim();

    try {
      const data = await adminLogin(trimmedUsername, password);
      setAdminToken(data.token);
      setAdminUsername(trimmedUsername);
      setToken(data.token);
      setAdminUsernameState(trimmedUsername);
      setPassword("");
    } catch {
      setLoginError("Hibás felhasználónév vagy jelszó.");
    } finally {
      setLoginLoading(false);
    }
  }

  function handleLogout() {
    clearAdminToken();
    setToken(null);
    setAdminUsernameState(null);
    setSubscribers([]);
    setUsername("");
    setPassword("");
  }

  if (!token) {
    return (
      <div className="coming-soon admin-panel">
        <main className="admin-panel-login">
          <img src={icons.logo} alt="Mizu logó" className="coming-soon-logo" />

          <h1 className="coming-soon-heading admin-panel-heading">Admin</h1>

          <div className="admin-panel-card">
            <form className="admin-panel-form" onSubmit={handleLogin}>
              <label className="visually-hidden" htmlFor="admin-username">
                Felhasználónév
              </label>
              <input
                id="admin-username"
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Felhasználónév"
                className="newsletter-modal-input"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />

              <label className="visually-hidden" htmlFor="admin-password">
                Jelszó
              </label>
              <input
                id="admin-password"
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Jelszó"
                className="newsletter-modal-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />

              {loginError && (
                <p className="newsletter-modal-error" role="alert">
                  {loginError}
                </p>
              )}
              {loadError && (
                <p className="newsletter-modal-error" role="alert">
                  {loadError}
                </p>
              )}

              <button
                type="submit"
                className="newsletter-modal-submit"
                disabled={loginLoading}
              >
                {loginLoading ? "Bejelentkezés…" : "Bejelentkezés"}
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <AdminShell
      section={section}
      adminUsername={adminUsername}
      onLogout={handleLogout}
    >
      {section === "preview" && <EmailPreview token={token} />}
      {section === "campaigns" && (
        <EmailCampaigns token={token} adminUsername={adminUsername} />
      )}
      {section === "settings" && (
        <AdminSettings token={token} adminUsername={adminUsername} />
      )}
      {section === "shop" && (
        <AdminShopSettings token={token} adminUsername={adminUsername} />
      )}
      {section === "orders" && <AdminOrders token={token} />}
      {section === "bookings" && <AdminBookings token={token} />}
      {section === "products" && <AdminProducts token={token} />}
      {section === "subscribers" && (
        <AdminDashboard
          subscribers={subscribers}
          loading={loadingList}
          refreshing={refreshing}
          onRefresh={refreshSubscribers}
          onDeleteSubscriber={handleDeleteSubscriber}
          onUpdateSubscriberStatus={handleUpdateSubscriberStatus}
          onImportSubscribers={handleImportSubscribers}
          onSendOpeningEmail={handleSendOpeningEmail}
          lastRefreshedAt={lastRefreshedAt}
        />
      )}
    </AdminShell>
  );
}

export default AdminPanel;
